import {
  BackgroundJobStateFilter,
  BackgroundJobStatisticsDto,
  BackgroundJobSummaryDto,
} from 'src/api/FiretrolApiClient';

// Hangfire reports the state as its own name rather than the filter enum, so
// matching is done on the lower-cased name.
const REQUEUEABLE = ['failed', 'deleted'];
const DELETABLE = ['enqueued', 'processing', 'scheduled', 'failed'];

export const canRequeueJob = (state: string): boolean =>
  REQUEUEABLE.includes(state.toLowerCase());

export const canDeleteJob = (state: string): boolean =>
  DELETABLE.includes(state.toLowerCase());

const STATE_COLORS: Record<string, string> = {
  enqueued: 'blue',
  scheduled: 'violet',
  processing: 'cyan',
  succeeded: 'green',
  failed: 'red',
  deleted: 'gray',
};

export const jobStateColor = (state: string): string =>
  STATE_COLORS[state.toLowerCase()] ?? 'gray';

/** The one timestamp worth showing for a row, given the state it is in. */
export const jobStateTimestamp = (
  job: BackgroundJobSummaryDto,
): Date | undefined => {
  switch (job.state.toLowerCase()) {
    case 'enqueued':
      return job.enqueuedAt ?? job.createdAt;
    case 'scheduled':
      return job.scheduledAt ?? job.createdAt;
    case 'processing':
      return job.startedAt ?? job.createdAt;
    case 'succeeded':
      return job.succeededAt ?? job.createdAt;
    case 'failed':
      return job.failedAt ?? job.createdAt;
    case 'deleted':
      return job.deletedAt ?? job.createdAt;
    default:
      return job.createdAt;
  }
};

export const JOB_STATE_FILTERS: Record<string, BackgroundJobStateFilter> = {
  Enqueued: BackgroundJobStateFilter.Enqueued,
  Scheduled: BackgroundJobStateFilter.Scheduled,
  Processing: BackgroundJobStateFilter.Processing,
  Succeeded: BackgroundJobStateFilter.Succeeded,
  Failed: BackgroundJobStateFilter.Failed,
  Deleted: BackgroundJobStateFilter.Deleted,
};

export const JOB_STATE_LABELS = Object.keys(JOB_STATE_FILTERS);

export const DEFAULT_JOB_STATE = 'Failed';

// `??` rather than `||`: the Enqueued member is 0.
export const jobStateFilterFor = (label: string): BackgroundJobStateFilter =>
  JOB_STATE_FILTERS[label] ?? BackgroundJobStateFilter.Failed;

/** The statistics keys are the lower-cased state labels. */
export const jobStateCount = (
  statistics: BackgroundJobStatisticsDto | undefined,
  label: string,
): number =>
  statistics?.[label.toLowerCase() as keyof BackgroundJobStatisticsDto] ?? 0;

export const formatJobDuration = (totalDurationMs?: number): string => {
  if (totalDurationMs === undefined) return '—';
  if (totalDurationMs < 1000) return `${Math.round(totalDurationMs)} ms`;
  return `${(totalDurationMs / 1000).toFixed(1)} s`;
};
